
import { getCommonPrintStyles, getHeaderHtml, getFooterHtml, getSignatureHtml } from "./printStyles";
import { downloadAsJPG, downloadAsPDF, createPrintDocument } from "./printingBridge";
import { toast } from "sonner";

const ITEMS_PER_PAGE = 15; // Keep existing limit

export interface RevenueStats {
  totalRevenue: number;
  paidAmount: number;
  pendingAmount: number;
  partialAmount: number;
  teacherSalaries: number;
}

export interface RevenueBreakdownItem {
  student: string;
  program: string;
  amount: number;
  status: string;
  enrollmentDate: string;
  nextPaymentDate: string;
}

export interface TeacherSalaryItem {
  teacherId: string;
  teacherName: string;
  department: string;
  paymentDate: string;
  paymentPeriod: string;
  amount: number;
  status: string;
}

export const printRevenueStatement = async (stats: RevenueStats, format: 'jpg' | 'pdf' = 'jpg') => {
  try {
    const loadingMessage = `Generating revenue statement ${format.toUpperCase()}...`;
    const loadingToast = toast.loading(loadingMessage);
    
    const { content, filename } = generateRevenueStatementContent(stats);
    
    if (format === 'pdf') {
      await downloadAsPDF(content, filename);
    } else {
      await downloadAsJPG(content, filename);
    }
    
    toast.dismiss(loadingToast);
    toast.success(`Revenue statement downloaded as ${format.toUpperCase()}!`);
    
  } catch (error) {
    console.error('Error downloading revenue statement:', error);
    toast.error('Failed to download revenue statement');
  }
};

const generateRevenueStatementContent = (stats: RevenueStats) => {
  const content = `
    <div class="print-page">
      ${getHeaderHtml('Revenue Statement')}
      
      <div class="print-content">
        <div class="stats-section">
          <h2>Financial Overview</h2>
          <div class="stats-grid">
            <div class="stat-item">
              <span class="stat-label">Total Revenue:</span>
              <span class="stat-value">AFN ${stats.totalRevenue.toLocaleString()}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Paid Amount:</span>
              <span class="stat-value paid">AFN ${stats.paidAmount.toLocaleString()}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Pending Amount:</span>
              <span class="stat-value pending">AFN ${stats.pendingAmount.toLocaleString()}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Partial Amount:</span>
              <span class="stat-value partial">AFN ${stats.partialAmount.toLocaleString()}</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">Teacher Salaries:</span>
              <span class="stat-value">AFN ${stats.teacherSalaries.toLocaleString()}</span>
            </div>
          </div>
        </div>
        
        ${getSignatureHtml('report')}
      </div>
      
      ${getFooterHtml('report')}
    </div>
  `;

  const styles = `
    ${getCommonPrintStyles()}
    .stats-section {
      margin: 40px 0;
    }
    .stats-section h2 {
      margin-bottom: 20px;
      color: #3D7069;
      font-size: 1.5em;
    }
    .stats-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 15px;
    }
    .stat-item {
      display: flex;
      justify-content: space-between;
      padding: 12px;
      border: 1px solid #ddd;
      border-radius: 4px;
      background: #f9f9f9;
    }
    .stat-label {
      font-weight: bold;
    }
    .stat-value {
      font-weight: bold;
      color: #333;
    }
    .stat-value.paid {
      color: #2e7d32;
    }
    .stat-value.pending {
      color: #ed6c02;
    }
    .stat-value.partial {
      color: #1976d2;
    }
  `;

  const printDocument = createPrintDocument(content, 'Revenue_Statement', styles);
  const filename = `Revenue_Statement_${new Date().toISOString().split('T')[0]}`;
  
  return { content: printDocument, filename };
};

export const printRevenueBreakdown = async (data: RevenueBreakdownItem[], format: 'jpg' | 'pdf' = 'jpg') => {
  try {
    const loadingMessage = `Generating revenue breakdown ${format.toUpperCase()}...`;
    const loadingToast = toast.loading(loadingMessage);
    
    const { content, filename } = generateRevenueBreakdownContent(data);
    
    if (format === 'pdf') {
      await downloadAsPDF(content, filename);
    } else {
      await downloadAsJPG(content, filename);
    }
    
    toast.dismiss(loadingToast);
    toast.success(`Revenue breakdown downloaded as ${format.toUpperCase()}!`);
    
  } catch (error) {
    console.error('Error downloading revenue breakdown:', error);
    toast.error('Failed to download revenue breakdown');
  }
};

const generateRevenueBreakdownContent = (data: RevenueBreakdownItem[]) => {
  const totalPages = Math.ceil(data.length / ITEMS_PER_PAGE);
  const pages: RevenueBreakdownItem[][] = [];
  
  for (let i = 0; i < data.length; i += ITEMS_PER_PAGE) {
    pages.push(data.slice(i, i + ITEMS_PER_PAGE));
  }
  
  const allPagesContent = pages.map((pageData, pageIndex) => {
    const currentPageNumber = pageIndex + 1;
    
    return `
      <div class="print-page ${pageIndex > 0 ? 'page-break' : ''}">
        ${getHeaderHtml(`Revenue Breakdown - Page ${currentPageNumber} of ${totalPages}`)}
        
        <div class="print-content">
          <div class="page-info">
            <strong>Page ${currentPageNumber} of ${totalPages}</strong> | 
            Records ${pageIndex * ITEMS_PER_PAGE + 1} - ${Math.min((pageIndex + 1) * ITEMS_PER_PAGE, data.length)} of ${data.length}
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Student</th>
                <th>Program</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Enrollment Date</th>
                <th>Next Payment</th>
              </tr>
            </thead>
            <tbody>
              ${pageData.map(item => `
                <tr>
                  <td>${item.student}</td>
                  <td>${item.program}</td>
                  <td>AFN ${item.amount.toLocaleString()}</td>
                  <td class="status-${item.status.toLowerCase()}">${item.status}</td>
                  <td>${new Date(item.enrollmentDate).toLocaleDateString()}</td>
                  <td>${item.nextPaymentDate ? new Date(item.nextPaymentDate).toLocaleDateString() : 'N/A'}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          ${getSignatureHtml('report')}
        </div>
        
        ${getFooterHtml('report')}
      </div>
    `;
  }).join('');

  const styles = `
    ${getCommonPrintStyles()}
    .page-info {
      text-align: center;
      font-size: 0.9em;
      color: #666;
      margin: 10px 0;
      padding: 8px;
      background: #f8f9fa;
      border-radius: 4px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 16px;
    }
    th, td {
      padding: 8px;
      border-bottom: 1px solid #ddd;
      text-align: left;
    }
    th {
      background: #f5f5f5;
      font-weight: bold;
    }
    .status-paid { color: #2e7d32; }
    .status-pending { color: #ed6c02; }
    .status-partial { color: #1976d2; }
  `;

  const printDocument = createPrintDocument(allPagesContent, 'Revenue_Breakdown', styles);
  const filename = `Revenue_Breakdown_${new Date().toISOString().split('T')[0]}`;
  
  return { content: printDocument, filename };
};

export const printTeacherSalaryReport = async (data: TeacherSalaryItem[], format: 'jpg' | 'pdf' = 'jpg') => {
  try {
    const loadingMessage = `Generating teacher salary report ${format.toUpperCase()}...`;
    const loadingToast = toast.loading(loadingMessage);
    
    console.log('Teacher salary data received:', data);
    
    // If no data provided, create sample data or show empty message
    if (!data || data.length === 0) {
      console.log('No teacher salary data found, creating empty report');
      const emptyContent = generateEmptyTeacherSalaryContent('No teacher salary records found.');
      
      if (format === 'pdf') {
        await downloadAsPDF(emptyContent.content, emptyContent.filename);
      } else {
        await downloadAsJPG(emptyContent.content, emptyContent.filename);
      }
      
      toast.dismiss(loadingToast);
      toast.success(`Teacher salary report downloaded as ${format.toUpperCase()}!`);
      return;
    }
    
    const { content, filename } = generateTeacherSalaryContent(data);
    
    if (format === 'pdf') {
      await downloadAsPDF(content, filename);
    } else {
      await downloadAsJPG(content, filename);
    }
    
    toast.dismiss(loadingToast);
    toast.success(`Teacher salary report downloaded as ${format.toUpperCase()}!`);
    
  } catch (error) {
    console.error('Error downloading teacher salary report:', error);
    toast.error('Failed to download teacher salary report');
  }
};

const generateEmptyTeacherSalaryContent = (message: string) => {
  const content = `
    <div class="print-page">
      ${getHeaderHtml('Teacher Salary Report')}
      
      <div class="print-content">
        <div class="empty-message">
          <h2>${message}</h2>
          <p>There are currently no teacher salary records to display.</p>
          <p>Add teacher payment records to generate salary reports.</p>
        </div>
        
        ${getSignatureHtml('report')}
      </div>
      
      ${getFooterHtml('report')}
    </div>
  `;

  const styles = `
    ${getCommonPrintStyles()}
    .empty-message {
      text-align: center;
      margin: 60px 0;
      padding: 40px;
      border: 2px dashed #ccc;
      border-radius: 8px;
      background: #f9f9f9;
    }
    .empty-message h2 {
      color: #666;
      margin-bottom: 15px;
      font-size: 1.3em;
    }
    .empty-message p {
      color: #888;
      margin-bottom: 10px;
      line-height: 1.5;
    }
  `;

  const printDocument = createPrintDocument(content, 'Teacher_Salary_Report_Empty', styles);
  const filename = `Teacher_Salary_Report_Empty_${new Date().toISOString().split('T')[0]}`;
  
  return { content: printDocument, filename };
};

const generateTeacherSalaryContent = (data: TeacherSalaryItem[]) => {
  console.log('Generating teacher salary content with data:', data);
  
  const totalPages = Math.ceil(data.length / ITEMS_PER_PAGE);
  const pages: TeacherSalaryItem[][] = [];
  
  for (let i = 0; i < data.length; i += ITEMS_PER_PAGE) {
    pages.push(data.slice(i, i + ITEMS_PER_PAGE));
  }
  
  const allPagesContent = pages.map((pageData, pageIndex) => {
    const currentPageNumber = pageIndex + 1;
    
    return `
      <div class="print-page ${pageIndex > 0 ? 'page-break' : ''}">
        ${getHeaderHtml(`Teacher Salary Report - Page ${currentPageNumber} of ${totalPages}`)}
        
        <div class="print-content">
          <div class="page-info">
            <strong>Page ${currentPageNumber} of ${totalPages}</strong> | 
            Records ${pageIndex * ITEMS_PER_PAGE + 1} - ${Math.min((pageIndex + 1) * ITEMS_PER_PAGE, data.length)} of ${data.length}
          </div>
          
          <table>
            <thead>
              <tr>
                <th>Teacher ID</th>
                <th>Teacher Name</th>
                <th>Department</th>
                <th>Payment Date</th>
                <th>Payment Period</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              ${pageData.map(item => {
                const formattedDate = item.paymentDate ? new Date(item.paymentDate).toLocaleDateString() : 'N/A';
                const formattedAmount = `AFN ${item.amount.toLocaleString()}`;
                const statusClass = item.status ? item.status.toLowerCase().replace(/\s+/g, '-') : 'pending';
                
                return `
                  <tr>
                    <td>${item.teacherId || 'N/A'}</td>
                    <td>${item.teacherName || 'N/A'}</td>
                    <td>${item.department || 'N/A'}</td>
                    <td>${formattedDate}</td>
                    <td>${item.paymentPeriod || 'N/A'}</td>
                    <td style="font-weight: bold;">${formattedAmount}</td>
                    <td class="status-${statusClass}">${item.status || 'pending'}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          
          ${getSignatureHtml('report')}
        </div>
        
        ${getFooterHtml('report')}
      </div>
    `;
  }).join('');

  const styles = `
    ${getCommonPrintStyles()}
    .page-info {
      text-align: center;
      font-size: 0.9em;
      color: #666;
      margin: 10px 0;
      padding: 8px;
      background: #f8f9fa;
      border-radius: 4px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 16px;
      font-size: 0.85em;
    }
    th, td {
      padding: 8px 6px;
      border-bottom: 1px solid #ddd;
      text-align: left;
      vertical-align: top;
    }
    th {
      background: #f5f5f5;
      font-weight: bold;
      border-bottom: 2px solid #ccc;
    }
    .status-paid,
    .status-completed {
      color: #2e7d32;
      font-weight: 500;
    }
    .status-pending {
      color: #ed6c02;
      font-weight: 500;
    }
    .status-partial {
      color: #1976d2;
      font-weight: 500;
    }
    .status-failed {
      color: #d32f2f;
      font-weight: 500;
    }
  `;

  const printDocument = createPrintDocument(allPagesContent, 'Teacher_Salary_Report', styles);
  const filename = `Teacher_Salary_Report_${new Date().toISOString().split('T')[0]}`;
  
  return { content: printDocument, filename };
};
